/**
 * Device-tier → render-resolution clamp (perf cost-census, increment 6).
 *
 * The office rendered at the full `window.devicePixelRatio`. Fill-rate scales with the SQUARE of the pixel
 * ratio, so a 2× retina panel costs 4× the fragments of a 1× panel — the single dominant GPU cost on weak
 * hardware. The landing backdrop (1.5×) and the character picker (2×) already clamp; the OFFICE — the one
 * surface that runs the whole session — did not, so a low-power laptop paid full freight every frame.
 *
 * We clamp by a coarse device tier:
 *   • STRONG hardware caps at 2× — the app's established quality bar (the picker renders the same critters
 *     at 2×) and the visual plateau: beyond 2× is imperceptible at normal viewing distance for large cost.
 *   • WEAK hardware (few cores / little RAM) caps at 1.5× — trading a sliver of sharpness for a ~44%
 *     fragment reduction exactly where FPS hurts.
 *
 * Detection is best-effort and CONSERVATIVE: anything we can't measure is treated as STRONG, so we never
 * degrade a device we failed to classify. `hardwareConcurrency` is ~universal; `deviceMemory` is
 * Chromium-only and defaults high when absent.
 */

export type DeviceTier = 'lite' | 'full';

/** A user's render-quality PREFERENCE: an explicit tier, or `auto` to use {@link detectDeviceTier}. */
export type RenderTierPref = 'auto' | DeviceTier;

/** Resolve a user preference to a concrete tier: `auto` consults the device, an explicit choice wins. */
export function resolveRenderTier(
  pref: RenderTierPref,
  nav?: { hardwareConcurrency?: number; deviceMemory?: number },
): DeviceTier {
  return pref === 'auto' ? detectDeviceTier(nav) : pref;
}

/** The pixel-ratio ceiling for each tier (see module doc). */
export const TIER_DPR_CEILING: Record<DeviceTier, number> = { lite: 1.5, full: 2 };

/**
 * Coarse device tier from cheap, widely-available signals. `lite` when the machine looks low-powered
 * (≤4 logical cores OR ≤4 GB RAM); `full` otherwise (including when the signals are unavailable).
 * Pure but for reading `navigator` — pass an override in tests.
 */
export function detectDeviceTier(nav?: { hardwareConcurrency?: number; deviceMemory?: number }): DeviceTier {
  const source =
    nav ?? (typeof navigator !== 'undefined' ? (navigator as typeof nav & Navigator) : undefined);
  if (!source) return 'full';
  const cores = source.hardwareConcurrency || 8; // 0/undefined → assume capable (don't degrade the unknown)
  const memory = source.deviceMemory ?? 8; // Chromium-only; absent → assume capable
  return cores <= 4 || memory <= 4 ? 'lite' : 'full';
}

/**
 * Render resolution to pass to Pixi's `app.init({ resolution })` for the OFFICE: the device pixel ratio
 * clamped to the tier ceiling. Strong devices at DPR ≤ 2 are unaffected; only 3×+ panels and the lite tier
 * see a change. `dpr`/`tier` are injectable for tests.
 */
export function officeRenderResolution(dpr?: number, tier?: DeviceTier): number {
  const ratio = dpr ?? ((typeof window !== 'undefined' && window.devicePixelRatio) || 1);
  const ceiling = TIER_DPR_CEILING[tier ?? detectDeviceTier()];
  return Math.min(ratio, ceiling);
}
